// ---------------------------------------------------------------------------
// SELEÇÃO MÚLTIPLA de CONDOMÍNIOS / CATEGORIAS na busca de /imoveis — só nas
// licenças de utils/buscaMultiSelecao (cs834).
//
// Mesma mecânica do `useCidadesMulti` (e do select de BAIRRO): o react-select
// vira `isMulti` com contador no placeholder e chips embaixo.
//
// PONTE COM O CÓDIGO ANTIGO: com UM marcado, o hook chama `onChangeUnico(opção)`
// e tudo segue pelo escalar de sempre (companyActual/categoryActual: condomínio
// no caminho da URL, `?categoria=`, H1, chips). Com 0 ou 2+, chama
// `onChangeUnico({ value: 0 })` e entrega os ids em `onIds` — a página manda a
// lista na busca (`id_empreendimentos`/`id_categorias`) e na URL.
//
// PORTÃO: `habilitado`. Fechado, `selectProps` é `{}` e `selecionados` é `[]`:
// o spread no <Select> não muda nada e o tenant comum segue byte a byte.
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseIdsQuery } from '../../../utils/buscaMultiSelecao';

export type OpcaoMulti = { label?: string; value?: any };

type Params = {
    habilitado: boolean;
    /** o escalar da página (companyActual / categoryActual). */
    valorUnico: OpcaoMulti | null | undefined | 0;
    onChangeUnico: (_opcao: OpcaoMulti) => void;
    /** ids com 2+ marcados; [] com 0 ou 1. */
    onIds?: (_ids: number[]) => void;
    /** "condomínio"/"condomínios" — contador do placeholder. */
    singular: string;
    plural: string;
    /** "selecionada(s)" em vez de "selecionado(s)". */
    feminino?: boolean;
    /** placeholder sem nada marcado. */
    vazio: string;
};

export type SelecaoMulti = {
    habilitado: boolean;
    /** o que aparece marcado (e nos chips): a lista com 2+, senão o escalar da página. */
    selecionados: OpcaoMulti[];
    remover: (_valor: any) => void;
    /** desmarca tudo sem mexer no escalar (o "Ver todos" da página já zerou ele). */
    limpar: () => void;
    /** semeia a seleção a partir de ids (ex.: `?condominios=` na carga da página). */
    semearPorIds: (_ids: number[], _opcoes: OpcaoMulti[]) => void;
    selectProps: Record<string, any>;
};

const valorValido = (o: any) => Number(o?.value) > 0;

export function useSelecaoMulti({
    habilitado,
    valorUnico,
    onChangeUnico,
    onIds,
    singular,
    plural,
    feminino,
    vazio,
}: Params): SelecaoMulti {
    const [itens, setItens] = useState<OpcaoMulti[]>([]);

    const unico = valorValido(valorUnico) ? (valorUnico as OpcaoMulti) : null;
    const selecionados = useMemo<OpcaoMulti[]>(
        () => (!habilitado ? [] : itens.length >= 2 ? itens : unico ? [unico] : []),
        [habilitado, itens, unico]
    );

    const aplicar = useCallback(
        (lista: OpcaoMulti[]) => {
            const vistos = new Set<number>();
            const arr = (Array.isArray(lista) ? lista : []).filter((o) => {
                const v = Number(o?.value);
                if (!(v > 0) || vistos.has(v)) return false;
                vistos.add(v);
                return true;
            });
            setItens(arr);
            onIds?.(arr.length >= 2 ? arr.map((o) => Number(o.value)) : []);
            // ponte com o escalar (ver cabeçalho)
            onChangeUnico(arr.length === 1 ? arr[0] : { value: 0 });
        },
        [onChangeUnico, onIds]
    );

    const remover = useCallback(
        (valor: any) => aplicar(selecionados.filter((o) => Number(o?.value) !== Number(valor))),
        [aplicar, selecionados]
    );

    const limpar = useCallback(() => {
        setItens([]);
        onIds?.([]);
    }, [onIds]);

    const semearPorIds = useCallback((idsSeed: number[], opcoes: OpcaoMulti[]) => {
        const desejados = parseIdsQuery((idsSeed || []).join(','));
        if (desejados.length < 2) return;
        const achadas = (Array.isArray(opcoes) ? opcoes : []).filter((o) => desejados.includes(Number(o?.value)));
        if (achadas.length) setItens(achadas);
    }, []);

    // Escalar posto de FORA da ponte (URL de um condomínio só, chip da linha de cima) com 2+
    // marcados: o escalar vence e a lista some — senão select e busca contariam histórias diferentes.
    const unicoValor = unico ? Number(unico.value) : 0;
    const itensRef = useRef(itens);
    itensRef.current = itens;
    useEffect(() => {
        if (!habilitado || !unicoValor || itensRef.current.length < 2) return;
        setItens([unico as OpcaoMulti]);
        onIds?.([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [habilitado, unicoValor]);

    const selectProps = useMemo(() => {
        if (!habilitado) return {};
        const n = selecionados.length;
        return {
            isMulti: true,
            closeMenuOnSelect: true,
            controlShouldRenderValue: false,
            hideSelectedOptions: false,
            isClearable: false,
            components: { ClearIndicator: null },
            placeholder: n ? `${n} ${n === 1 ? singular : plural} selecionad${feminino ? 'a' : 'o'}${n === 1 ? '' : 's'}` : vazio,
            value: selecionados,
            onChange: (valor: any) => aplicar(Array.isArray(valor) ? valor : valor ? [valor] : []),
        };
    }, [habilitado, selecionados, aplicar, singular, plural, feminino, vazio]);

    return { habilitado, selecionados, remover, limpar, semearPorIds, selectProps };
}
